// Cookie consent helper. Stores granular consent in localStorage and
// dispatches a `consentchange` CustomEvent so other modules can react.

export type ConsentCategories = {
  necessary: true; // always true (locked)
  analytics: boolean;
  functional: boolean;
};

export type StoredConsent = ConsentCategories & {
  timestamp: number;
  version: number;
};

export const CONSENT_VERSION = 1;
const STORAGE_KEY = "cookie_consent_v1";
const EVENT_NAME = "consentchange";

export const defaultDenied: ConsentCategories = {
  necessary: true,
  analytics: false,
  functional: false,
};

export const defaultAllowed: ConsentCategories = {
  necessary: true,
  analytics: true,
  functional: true,
};

export function getConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(categories: ConsentCategories): StoredConsent {
  const payload: StoredConsent = {
    ...categories,
    necessary: true,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota errors
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: payload }));
  }
  return payload;
}

export function hasConsent(category: keyof ConsentCategories): boolean {
  const c = getConsent();
  if (!c) return false;
  return Boolean(c[category]);
}

export function onConsentChange(cb: (c: StoredConsent) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<StoredConsent>).detail);
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

export function openCookieSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("opencookiesettings"));
  }
}
